import { useState } from "react";
import { api } from "../../api";
import type { RelationRow } from "../../types";
import Icon from "../../components/Icon";

type RelationsEditorProps = {
  assetId: string;
  initialRelations: RelationRow[];
};

type Direction = "out" | "in" | "both";

const MONO: React.CSSProperties = { fontFamily: "var(--font-mono)", fontSize: 12 };

const EYEBROW: React.CSSProperties = {
  fontSize: 11,
  fontFamily: "var(--font-mono)",
  textTransform: "uppercase" as const,
  letterSpacing: "0.1em",
  color: "var(--fg-muted)",
  fontWeight: 500,
};

export default function RelationsEditor({ assetId, initialRelations }: RelationsEditorProps) {
  const [relations, setRelations] = useState<RelationRow[]>(initialRelations);
  const [direction, setDirection] = useState<Direction>("out");
  const [loadingDir, setLoadingDir] = useState(false);
  const [adding, setAdding] = useState(false);
  const [targetId, setTargetId] = useState("");
  const [relationType, setRelationType] = useState("");
  const [addSaving, setAddSaving] = useState(false);
  const [deletingKey, setDeletingKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function changeDirection(dir: Direction) {
    if (dir === direction) return;
    setDirection(dir);
    setLoadingDir(true);
    setError(null);
    try {
      const res = await api.getRelations(assetId, dir);
      setRelations(res.relations);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoadingDir(false);
    }
  }

  async function addRelation() {
    if (!targetId.trim() || !relationType.trim()) return;
    setAddSaving(true);
    setError(null);
    try {
      const res = await api.createRelation(assetId, {
        target_asset_id: targetId.trim(),
        relation_type: relationType.trim(),
      });
      setRelations((prev) => [
        ...prev,
        {
          source_asset_id: res.source_asset_id,
          target_asset_id: res.target_asset_id,
          relation_type: res.relation_type,
          sort_order: prev.length,
          created_at: null,
        },
      ]);
      setTargetId("");
      setRelationType("");
      setAdding(false);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setAddSaving(false);
    }
  }

  async function deleteRelation(targetAssetId: string, relType: string) {
    const key = `${targetAssetId}|${relType}`;
    setDeletingKey(key);
    setError(null);
    try {
      await api.deleteRelation(assetId, targetAssetId, relType);
      setRelations((prev) =>
        prev.filter(
          (r) => !(r.target_asset_id === targetAssetId && r.relation_type === relType)
        )
      );
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setDeletingKey(null);
    }
  }

  return (
    <div className="field-group" style={{ marginBottom: 16 }}>
      <div className="field-group-head">
        <span style={EYEBROW}>Relations</span>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ ...MONO, color: "var(--fg-muted)" }}>{relations.length}</span>
          <button
            className="btn btn-quiet btn-sm"
            onClick={() => { setAdding(true); setError(null); }}
            style={{ fontSize: 12 }}
          >
            <Icon name="plus" size={12} /> Add
          </button>
        </div>
      </div>

      {/* Direction filter */}
      <div style={{ marginBottom: 10 }}>
        <div className="chip-filter">
          {(["out", "in", "both"] as Direction[]).map((d) => (
            <button
              key={d}
              className={direction === d ? "on" : ""}
              onClick={() => void changeDirection(d)}
              disabled={loadingDir}
            >
              {d}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div style={{ marginBottom: 8, fontSize: 12, ...MONO, color: "#e58a6d" }}>{error}</div>
      )}

      {loadingDir ? (
        <div style={{ fontSize: 13, color: "var(--fg-subtle)" }}>Loading…</div>
      ) : relations.length > 0 ? (
        <table className="table" style={{ marginBottom: adding ? 12 : 0 }}>
          <thead>
            <tr>
              <th>Source</th>
              <th>Target</th>
              <th>Type</th>
              <th style={{ width: 40 }} />
            </tr>
          </thead>
          <tbody>
            {relations.map((r) => {
              const key = `${r.target_asset_id}|${r.relation_type}`;
              return (
                <tr key={key} style={{ cursor: "default" }}>
                  <td style={{ ...MONO }}>{r.source_asset_id}</td>
                  <td style={{ ...MONO }}>{r.target_asset_id}</td>
                  <td style={{ fontSize: 13 }}>{r.relation_type}</td>
                  <td>
                    <button
                      className="btn btn-quiet btn-sm"
                      onClick={() => void deleteRelation(r.target_asset_id, r.relation_type)}
                      disabled={deletingKey === key}
                      style={{ fontSize: 12, color: "var(--fg-muted)" }}
                    >
                      {deletingKey === key ? "…" : <Icon name="trash" size={12} />}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        !adding && (
          <div style={{ fontSize: 13, color: "var(--fg-subtle)", fontStyle: "italic" }}>
            No relations in this direction.
          </div>
        )
      )}

      {adding && (
        <div
          style={{
            background: "var(--surface-raised)",
            border: "1px solid var(--rule)",
            borderRadius: "var(--r-sm)",
            padding: "12px",
            display: "grid",
            gridTemplateColumns: "1fr 1fr auto",
            gap: 8,
            alignItems: "end",
          }}
        >
          <div>
            <div style={{ ...EYEBROW, marginBottom: 4 }}>Target asset ID *</div>
            <input
              className="input input-mono"
              value={targetId}
              onChange={(e) => setTargetId(e.target.value)}
              placeholder="uuid"
              autoFocus
              style={{ fontSize: 12 }}
            />
          </div>
          <div>
            <div style={{ ...EYEBROW, marginBottom: 4 }}>Relation type *</div>
            <input
              className="input"
              value={relationType}
              onChange={(e) => setRelationType(e.target.value)}
              placeholder="e.g. related_to"
              style={{ fontSize: 13 }}
            />
          </div>
          <div style={{ display: "flex", gap: 4 }}>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => void addRelation()}
              disabled={addSaving || !targetId.trim() || !relationType.trim()}
              style={{ fontSize: 12 }}
            >
              {addSaving ? "Adding…" : "Add"}
            </button>
            <button
              className="btn btn-quiet btn-sm"
              onClick={() => { setAdding(false); setTargetId(""); setRelationType(""); }}
              style={{ fontSize: 12 }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
